// @ts-nocheck
import { useEffect, useState } from 'react';
import { Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_ACL_FREEZE_PASSWORD } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';

export const SUPERADMIN_ACL_NAME = 'superadmin_acl';

/** Locked by rule — the is_locked flag cannot turn these off. */
export const isPermanentlyLockedAcl = (acl: any) =>
  Boolean(acl?.default_for_role) || acl?.acl_name === SUPERADMIN_ACL_NAME;

export const isAclLocked = (acl: any) =>
  Boolean(acl?.is_locked) || isPermanentlyLockedAcl(acl);

export const LockBadge = ({ acl }: { acl: any }) => {
  if (!isAclLocked(acl)) return null;
  const permanent = isPermanentlyLockedAcl(acl);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400"
      title={
        permanent
          ? 'Frozen permanently — default ACLs can never be unlocked'
          : 'Frozen — Super Admin must enter the freeze password to change it'
      }
    >
      <Lock className="h-3 w-3" />
      Frozen
    </span>
  );
};

export const FreezePasswordPrompt = ({
  open,
  onOpenChange,
  aclName,
  actionLabel = 'Continue',
  description,
  onConfirm,
}: any) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (!password.trim()) {
      toast.error('Enter the freeze password.');
      return;
    }
    setBusy(true);
    try {
      await onConfirm(password.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600">
              <Lock className="h-4 w-4" />
            </span>
            Frozen ACL
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {description || (
            <>
              <span className="font-medium text-foreground">{aclName}</span> is frozen. Enter
              the freeze password to continue.
            </>
          )}
        </p>

        <Input
          type="password"
          autoFocus
          value={password}
          placeholder="Freeze password"
          onChange={(e: any) => setPassword(e.target.value)}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter') submit();
          }}
        />

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={submit}>
            {busy ? 'Checking…' : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FreezePasswordManager = ({ open, onOpenChange }: any) => {
  const [status, setStatus] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewPassword('');
    setConfirmPassword('');
    getApiCall(API_ACL_FREEZE_PASSWORD, {})
      .then((resp: any) => setStatus(resp))
      .catch(() => setStatus(null));
  }, [open]);

  const save = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      const resp = await postApiCall(API_ACL_FREEZE_PASSWORD, {
        new_password: newPassword,
      });
      toast.success('Freeze password set.');
      setStatus({ ...(status || {}), is_set: true });
      setNewPassword('');
      setConfirmPassword('');
      return resp;
    } catch (err: any) {
      toast.error(friendlyApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldAlert className="h-4 w-4" />
            </span>
            Freeze Password
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {status?.is_set
            ? 'A freeze password is already set and cannot be changed from here.'
            : 'No freeze password is set yet. Set one to start freezing ACLs.'}
          {status?.set_by_name ? ` Last set by ${status.set_by_name}.` : ''}
        </p>

        {!status?.is_set && (
          <div className="space-y-2">
            <Input
              type="password"
              value={newPassword}
              placeholder={`Password (min ${status?.min_length || 8} chars, letters + numbers)`}
              onChange={(e: any) => setNewPassword(e.target.value)}
            />
            <Input
              type="password"
              value={confirmPassword}
              placeholder="Confirm password"
              onChange={(e: any) => setConfirmPassword(e.target.value)}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {!status?.is_set && (
            <Button disabled={busy || !newPassword} onClick={save}>
              Set password
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
